import type { BaseEnvelope, BasePatchOp } from './base-patch';
import type { AppliedFrame, AppliedNewInventory } from '../transport/applied-frame';
import { encounterCreatureField, encounterField } from '../../shared/array-types';
import { fromBaseRecord, type FromBaseRow as Row } from './from-base-context';
import { readInventoryField, readInventoryRows } from './inventory-raw-readers';
import { readCreatureStatuses } from './creature-status-reader';
import { readItemField } from './item-field-reader';
import { sameSavedInventoryColumn } from './saved-equipment-delta';
import { relocationDefinitionsUnchanged } from './saved-creature-relocation';
import { savedPayloadMemberFrame } from './saved-payload-member-delta';
import { savedInventoryBirthFrame } from './saved-inventory-birth-delta';
import { compileInventoryTreeFromBase, type FromBaseInventoryTree } from './from-base-inventory';
import { inventoryRuntimeMetadata } from './inventory-runtime-metadata';
import { encounterMemberSharedInputs } from './encounter-member-input';

type MoveContext = { envelope: BaseEnvelope; computed: unknown };
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const id = (row: Row): string => upper(readInventoryField(row, 'inventory_id'));
const owner = (row: Row): string => upper(readInventoryField(row, 'possess_id'));
const memberId = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
const moveColumns = new Set(['inventory_id', 'possess_id', 'inventory_deleted', 'equipped', 'inventory_wares',
    'x_pos', 'y_pos', 'z_pos', 'inventory_t_updated', 'inventory_updated_by_id']);

/** A saved whole floor pickup already has its complete closure in the held room.
 * Compile only each relocated subtree for its new possessor; reuse birth's carry
 * differ and the native member/resource differ. No SELECT or enclosing compile. */
export async function savedInventoryMoveFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], computed: unknown,
    context: MoveContext = { envelope, computed }): Promise<{ frame: AppliedFrame; inputs: Row } | null> {
    try { return await inventoryMoveFrame(envelope, ops, computed, context); }
    catch { return null; }
}

async function inventoryMoveFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], computed: unknown,
    context: MoveContext): Promise<{ frame: AppliedFrame; inputs: Row } | null> {
    const scene = context.envelope, before = scene.base, output = context.computed;
    if (scene.entity !== 'encounter' || !fromBaseRecord(output) || !scene.metadata
        || !ops.length || ops.some(op => !['inventory', 'items', 'item_layer_types', 'statuses', 'encounter_creatures'].includes(op.table))) return null;
    const eid = upper(encounterField(before, 'encounter_id')) || upper(scene.entity_id);
    const writes = ops.filter(op => op.table === 'inventory');
    if (!writes.length || writes.some(op => op.op !== 'upsert') || !await relocationDefinitionsUnchanged(before, ops)) return null;
    const members = Array.isArray(before.encounter_creatures_arr) ? before.encounter_creatures_arr.filter(fromBaseRecord) : [];
    const floor = readInventoryRows(before, 'inventory');
    const arrivals = new Map<string, AppliedNewInventory>();
    const inventoryOps: BasePatchOp[] = [];
    for (const op of writes) {
        const saved = floor.find(row => id(row) === upper(op.row_id));
        if (!saved || owner(saved) !== eid || Number(readInventoryField(saved, 'inventory_deleted') ?? 0)) return null;
        const cols = { ...saved.inventory as Row, ...op.cols };
        const destination = owner({ inventory: cols }), target = members.find(row => memberId(row) === destination);
        if (!target || Number(readInventoryField({ inventory: cols }, 'inventory_deleted') ?? 0)
            || readInventoryField({ inventory: cols }, 'equipped')
            || Object.keys(op.cols).some(key => !moveColumns.has(key)
                && !sameSavedInventoryColumn(key, readInventoryField(saved, key), readInventoryField({ inventory: op.cols }, key)))) return null;
        const family = new Set([upper(op.row_id)]);
        let count: number;
        do {
            count = family.size;
            for (const row of floor) if (family.has(owner(row))) family.add(id(row));
        } while (family.size !== count);
        if ([...arrivals.values()].some(arrival => arrival.base.some(row => family.has(id(row))))) return null;
        const savedRows = floor.filter(row => family.has(id(row))).map(row => id(row) === upper(op.row_id)
            ? { ...row, inventory: cols } : row);
        const catalog = fromBaseRecord(before.catalog) ? before.catalog : {};
        const input = { ...encounterMemberSharedInputs(before), entity_id: destination, base_rev: 0,
            inventory: savedRows, metadata: inventoryRuntimeMetadata(scene.metadata as Parameters<typeof inventoryRuntimeMetadata>[0]),
            inventory_order: savedRows.map(row => ({ ...row.inventory as Row,
                item_name: readItemField(catalog[upper(readInventoryField(row, 'inventory_item_id'))] as Row ?? null, 'item_name') })),
            possessor: target, encounter_links: before.encounter_links };
        const compiled = compileInventoryTreeFromBase(input as unknown as FromBaseInventoryTree, catalog as Record<string, Row>);
        if (compiled.missingStages.length || compiled.inventory?.length !== 1) return null;
        arrivals.set(upper(op.row_id), { entity_id: upper(op.row_id), possess_id: destination, base: savedRows, node: compiled.inventory[0]! });
        inventoryOps.push({ ...op, cols });
    }
    // Native tails precede the saved-row relay. Their already-retired SQL
    // tombstones must not force a second status pass or a room reload.
    const hosts = [before, ...members, ...floor, ...members.flatMap(row => readInventoryRows(row, 'inventory'))];
    const heldStatuses = new Set(hosts.flatMap(row => (['target', 'caster'] as const)
        .flatMap(side => Object.values(readCreatureStatuses(row, side))).filter(fromBaseRecord)
        .map(status => upper(readItemField(status, 'status_id')))));
    const memberOps = ops.filter(op => !['inventory', 'items', 'item_layer_types'].includes(op.table)
        && !(op.table === 'statuses' && op.op === 'delete' && !heldStatuses.has(upper(op.row_id))));
    const member = memberOps.length ? await savedPayloadMemberFrame(scene, memberOps, output) : null;
    if (memberOps.length && !member) return null;
    const birth = await savedInventoryBirthFrame(scene, inventoryOps, output, new Set(), arrivals);
    if (!birth || scene.base !== before) return null;
    const delta = { ...birth.frame.computed_delta };
    for (const [id, patch] of Object.entries(member?.computed_delta ?? {})) {
        const prior = delta[id];
        delta[id] = prior ? { ...prior, ...patch, set: [...prior.set, ...patch.set], unset: [...prior.unset, ...patch.unset] } : patch;
    }
    const rawCatalog = fromBaseRecord(before.catalog) ? before.catalog : {};
    const definitions = Object.fromEntries([...new Set([...arrivals.values()].flatMap(arrival =>
        Object.keys(fromBaseRecord(arrival.node.catalog) ? arrival.node.catalog : {})))].filter(id => id in rawCatalog)
        .map(id => [id, rawCatalog[id]]));
    return { inputs: birth.inputs, frame: { ...birth.frame,
        operation_id: `saved-inventory-move:${envelope.entity}:${upper(envelope.entity_id)}:${ops[0]!.base_rev}`,
        rows: [...birth.frame.rows, ...member?.rows ?? []],
        inputs: { ...birth.frame.inputs, catalog: definitions }, computed_delta: delta,
        deletes: [...arrivals.values()].flatMap(arrival => arrival.base.map(row => ({ table: 'inventory', row_id: id(row) })))
            .concat(member?.deletes ?? []) } };
}
